import {Injectable} from '@angular/core';
import {from, Observable, of, TimeoutError} from 'rxjs';
import {HttpClient, HttpEvent, HttpEventType, HttpProgressEvent, HttpResponse} from '@angular/common/http';
import {AlertController, Platform} from '@ionic/angular';
import {CurrentSettings, StateInterface} from '../../interfaces/biozoom-api/app-configuration';
import {SerialDispatcher} from '../../methods/serial-dispatcher';
import {ScannerStatus} from '../../interfaces/scanner-api/scanner-status';
import {catchError, delay, filter, first, repeat, switchMap, timeout} from 'rxjs/operators';
import {PipelineStageConfig, StatusObject} from '../../interfaces/biozoom-api/pipeline';
import {
  blockifyUpdate,
  evalJsonWrapper,
  hexToBin,
  hexToBinNuv,
  parseHandRecognition,
  readBlobAsText
} from '../../methods/blockFormat';
import {updateESPNeeded, updateInfNeeded, updateNUVNeeded} from '../../methods/helpers';

@Injectable({
  providedIn: 'root'
})
export class UsbApiService2 {
  // private lastReadSerial: string;
  // private serialRegEx = '\\d[A-Z][0-9A-F]{18}';
  private oldSerialRegEx = '[45]W[0-9A-F]{18}';
  // private serial: Serial;
  private isUSB = false;
  private firstConnection = false;

  constructor(
    private platform: Platform,
    private alertCtrl: AlertController,
    private http: HttpClient,
  ) {
  }


  async enableAP(state: StateInterface) {
    let serialDispatcher1 = new SerialDispatcher(state.CONNECTION_PARAMS, this.platform, state.SCANNER_VERSION, state.SCANNER_SERIAL);
    await serialDispatcher1.write('mode?set=user');
    const [status1, res1] = await serialDispatcher1.getResult();
    await new Promise((r) => setTimeout(r, 1000));
    await serialDispatcher1.close();
    await new Promise((r) => setTimeout(r, 500));

    await serialDispatcher1.open();
    await new Promise((r) => setTimeout(r, 3000));

    // await serialDispatcher1.write('restart');
    serialDispatcher1 = null;
    // await new Promise((r) => setTimeout(r, 3000));

    let serialDispatcher = new SerialDispatcher(state.CONNECTION_PARAMS, this.platform, state.SCANNER_VERSION, state.SCANNER_SERIAL);
    await serialDispatcher.write('ap?mode=on');
    const [status, res] = await serialDispatcher.getResult();
    serialDispatcher = null;
    try {
      const r = JSON.parse(res);
      return { status, wifi: r.ap_wifi };
    } catch (e) {
      return { status: false, wifi: '' };
    }
  }

  async disableAP(state: StateInterface) {
    let serialDispatcher1 = new SerialDispatcher(state.CONNECTION_PARAMS, this.platform, state.SCANNER_VERSION, state.SCANNER_SERIAL);
    // await serialDispatcher1.write('mode?set=production');
    // await serialDispatcher1.getResult();
    // await new Promise((r) => setTimeout(r, 500));
    await serialDispatcher1.close();
    await new Promise((r) => setTimeout(r, 500));
    await serialDispatcher1.open();
    await new Promise((r) => setTimeout(r, 3000));
    serialDispatcher1 = null;
    return;
  }

  async deleteFlash(state: StateInterface) {
    let serialDispatcher = new SerialDispatcher(state.CONNECTION_PARAMS, this.platform, state.SCANNER_VERSION, state.SCANNER_SERIAL);
    if ((state.SCANNER_SERIAL ?? '').startsWith('2N')) {
      await serialDispatcher.write('e\n');
    } else {
      await serialDispatcher.write('erase-chip\n');
    }
    await new Promise(r => setTimeout(r, 10000));
    await serialDispatcher.close();
    await new Promise(r => setTimeout(r, 500));
    let check = false;
    for (let count = 0; count < 60; count++) {
      await new Promise(r => setTimeout(r, 500));
      await serialDispatcher.open();
      await new Promise(r => setTimeout(r, 500));
      const result = await serialDispatcher.getResult();
      if (result[1] === 'Disconnected.') {
        continue;
      }
      check = true;
      break;
    }
    if (!check) {
      return false;
    }
    await new Promise(r => setTimeout(r, 3000));
    if (!(state.SCANNER_SERIAL ?? '').startsWith('2N')) {
      await serialDispatcher.write('restart\n');
      await new Promise(r => setTimeout(r, 5000));
    }
    await serialDispatcher.write('status\n');
    await new Promise(r => setTimeout(r, 2000));
    serialDispatcher = null;
    return true;
  }

  updateInf(pipelineConfig: PipelineStageConfig, bootloader: boolean) {
    return switchMap((_stati: StatusObject): Observable<{started: boolean, target: string}> => {

      const shouldUpdate = updateInfNeeded(
        pipelineConfig?.state?.persistentValues?.SCANNER_VERSION,
        pipelineConfig?.state?.persistentValues?.SCANNER_STATUS?.sensor_firmware,
        pipelineConfig?.state?.persistentValues?.SCANNER_STATUS?.wifi_firmware,
        pipelineConfig.config
      );
      console.log(shouldUpdate);

      const state = pipelineConfig.state.persistentValues;
      if (state.SCANNER_VERSION === 2 && shouldUpdate) {
        return this.update(pipelineConfig, state, bootloader ? 'inf-bootloader' : 'inf').pipe(
          switchMap((event) => {
            // console.log(event, event.type);
            switch (event.type) {
              case HttpEventType.Response: {
                if (event.body === '') {
                  return of(false);
                } else if (event.body === 'Error.') {
                  return of([true, false]);
                }
                pipelineConfig.setStep({ progress: 1, idx: 0 });
                return of([true, true]);
              }
              case HttpEventType.DownloadProgress: {
                pipelineConfig.setStep({ progress: (event.loaded / event.total), idx: 0 });
                return of([false, false]);
              }
              default: {
                return of([false, false]);
              }
            }
          }),
          filter((el) => !!el[0]),
          first(),
          switchMap((el) => {
            if (!el[1]) {
              return of({started: true, target: 'onError'});
            }
            return of({started: true, target: 'onSuccess'});
          })
        );
      }
      // pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onNotNeeded, 'onNotNeeded', pipelineConfig.stage);
      return of({started: false, target: 'onNotNeeded'});
    });
  }

  updateNuv(pipelineConfig: PipelineStageConfig) {
    return switchMap((_stati: StatusObject): Observable<boolean> => {
      const shouldUpdate = updateNUVNeeded(
        pipelineConfig?.state?.persistentValues?.SCANNER_SERIAL,
        pipelineConfig.state.persistentValues.SCANNER_STATUS.nuvoton_firmware,
        pipelineConfig.config
      );
      const state = pipelineConfig.state.persistentValues;
      if (state.SCANNER_VERSION === 2 && shouldUpdate) {
        return this.update(pipelineConfig, state, 'nuv').pipe(
          switchMap((event) => {
            switch (event.type) {
              case HttpEventType.Response: {
                if (event.body === '') {
                  return of(false);
                } else if (event.body === 'Error.') {
                  return of([true, false]);
                }
                pipelineConfig.setStep({ progress: 1, idx: 0 });
                return of([true, true]);
              }
              case HttpEventType.DownloadProgress: {
                pipelineConfig.setStep({ progress: (event.loaded / event.total), idx: 0 });
                return of([false, false]);
              }
              default: {
                return of([false, false]);
              }
            }
          }),
          filter((el) => !!el[0]),
          first(),
          switchMap((el) => {
            if (el[1]) {
              pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
            } else {
              pipelineConfig.executeStage(
                pipelineConfig?.stageConfig?.eventTargets?.onError,
                'Error',
                pipelineConfig.stage
              );
            }
            return of(true);
          })
        );
      }
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onNotNeeded, 'onNotNeeded', pipelineConfig.stage);
      return of(true);
    });
  }

  updateESP(pipelineConfig: PipelineStageConfig) {
    return switchMap((_stati: StatusObject): Observable<{started: boolean, target: string}> => {

      const shouldUpdate = updateESPNeeded(
        pipelineConfig?.state?.persistentValues?.SCANNER_SERIAL,
        pipelineConfig.state.persistentValues.SCANNER_STATUS.wifi_firmware,
        pipelineConfig.config
      );

      const state = pipelineConfig.state.persistentValues;
      if (state.SCANNER_VERSION === 2 && shouldUpdate) {
        return this.update(pipelineConfig, state, 'esp').pipe(
          switchMap((event) => {
            switch (event.type) {
              case HttpEventType.Response: {
                if (event.body === '') {
                  return of([false, false]);
                } else if (event.body === 'Error.') {
                  return of([true, false]);
                }
                pipelineConfig.setStep({ progress: 1, idx: 0 });
                return of([true, true]);
              }
              case HttpEventType.DownloadProgress: {
                pipelineConfig.setStep({ progress: (event.loaded / event.total), idx: 0 });
                return of([false, false]);
              }
              default: {
                return of([false, false]);
              }
            }
          }),
          filter((el) => !!el[0]),
          first(),
          switchMap((el) => {
            if (el[1]) {
              return of({
                started: true,
                target: 'onSuccess',
              });
              // pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
            } else {
              return of({
                started: true,
                target: 'onError',
              });
              // pipelineConfig.executeStage(
              //   pipelineConfig?.stageConfig?.eventTargets?.onError,
              //   'Error',
              //   pipelineConfig.stage
              // );
            }
            // return of(true);
          })
        );
      }
      // pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onNotNeeded, 'onNotNeeded', pipelineConfig.stage);
      return of({
        started: false,
        target: 'onNotNeeded',
      });
    });
  }

  update(pipelineConfig: PipelineStageConfig, state: StateInterface, target: string = 'esp'): Observable<HttpEvent<any>>  {
    const initFunc = async () => {
      let updateCmd: string;
      if (target === 'esp') {
        const x = state.SCANNER_STATUS;
        const major = Number(x.wifi_firmware.split('.')[0]);
        const filename = (major >= 20 ? pipelineConfig.config.firmware_20_filename : pipelineConfig.config.firmware_10_filename);
        const fileP = (this.http.get(`/assets/firmware/${filename}`, {responseType: 'blob'})).toPromise();
        const binFile = await fileP;
        const updateRaw = await binFile.arrayBuffer();
        const updateArr = new Uint8Array(updateRaw);
        const updateStr = blockifyUpdate(updateArr);
        updateCmd = `updateData?length=${binFile.size}&type=espressif&data=${updateStr}`;
      } else if (target === 'nuv') {
          // const x = state.SCANNER_STATUS;
          const filename = pipelineConfig.config.firmware_nuvoton_filename;
          const blob = await this.http.get(`/assets/firmware/${filename}`, {
            responseType: 'blob',
            headers: { Accept: 'text/plain' }
          }).toPromise();

          const binFile = await readBlobAsText(blob);
          // console.log(binFile);
          const blocks: string[] = hexToBinNuv(binFile);
          const length = blocks.map((el) => el.length).reduce((a, b) => a + b, 0);
          updateCmd = `updateData?length=${length}&type=nuvoton&data=${blocks.join(',')}`;
      } else if (target === 'inf') {
        const filename = pipelineConfig.config.sensor_filename;
        const blob = await this.http.get(`/assets/firmware/${filename}`, {
          responseType: 'blob',
          headers: { Accept: 'text/plain' }
        }).toPromise();

        const binFile = await readBlobAsText(blob);
        const {dataBin, startAddr} = hexToBin(binFile);
        const updateStr = blockifyUpdate(dataBin);
        updateCmd = `updateData?length=${dataBin.byteLength}&type=infineon&startAddr=${startAddr}&data=${updateStr}`;
      } else if (target === 'inf-bootloader') {
        const filename = 'bootloader.hex';
        const blob = await this.http.get(`/assets/firmware/${filename}`, {
          responseType: 'blob',
          headers: { Accept: 'text/plain' }
        }).toPromise();

        const binFile = await readBlobAsText(blob);
        const {dataBin, startAddr} = hexToBin(binFile, 4096);
        const updateStr = blockifyUpdate(dataBin);
        updateCmd = `updateData?length=${dataBin.byteLength}&type=infineonBootloader&update=false&startAddr=${startAddr}&data=${updateStr}`;
      }
      const serialDispatcherInit = new SerialDispatcher(state.CONNECTION_PARAMS, this.platform, state.SCANNER_VERSION, state.SCANNER_SERIAL);
      await serialDispatcherInit.update(updateCmd);
      await new Promise((r) => setTimeout(r, 1000));
      return serialDispatcherInit;
    };

    let serialDispatcher: SerialDispatcher;
    let lastNum = 0;
    let lastTime = Date.now();

    return from(initFunc()).pipe(
      switchMap(async _ => {
        serialDispatcher = _;
        return await serialDispatcher.getUpdateProgress();
      }),
      switchMap(async prog => {
        const now = Date.now();
        if (prog > lastNum) {
          lastNum = prog;
          lastTime = now;
        }
        if ((now - lastTime) > 60000) {
          throw new TimeoutError();
        }
        if (prog < 0) {
          serialDispatcher = null;
          return new HttpResponse({ body: 'Error.' });
        }
        else if (prog < 100) {
          return { type: HttpEventType.DownloadProgress, loaded: prog, total: 100 } as HttpProgressEvent;
        } else {
          serialDispatcher = null;
          return new HttpResponse({ body: 'Success.' });
        }
      }),
      delay(100),
      timeout(60000),
      repeat(),
      catchError((err) => {
        return of(new HttpResponse({ body: '' }));
      })
    );
  }


  /**
   * Performs hand recognition based on the given settings.
   * @param settings - The current settings for hand recognition.
   * @returns An Observable that emits a boolean value indicating if hand recognition was successful.
   */
  async doHandrecognitionObservable(state: StateInterface, settings: CurrentSettings, handOff = false, newProtocol: boolean): Promise<boolean> {
    if (newProtocol) {

      const start = handOff ? 134 : 110;
      const count = handOff ? 10 : 13;
      const cycles = handOff ? 25 : 5;
      let serialDispatcherInner = new SerialDispatcher(state.CONNECTION_PARAMS, this.platform, state.SCANNER_VERSION, state.SCANNER_SERIAL);
      await serialDispatcherInner.write(`hand?start=${start}&count=${count}&cycles=${cycles}`);
      const [statusInner, resInner] = await serialDispatcherInner.getResult();
      serialDispatcherInner = null;
      if (!statusInner) {
        return false;
      }
      try {

        const jsonParsed = JSON.parse(resInner);
        const result = parseHandRecognition(jsonParsed.data, handOff, start, count, cycles);
        if (result) {
          const dataToSend = [];
          for (let i = 0; i < jsonParsed.data.length; i++) {
            if (((i + 1) % (cycles + 2)) === 0) {
              continue;
            }
            dataToSend.push(jsonParsed.data[i]);
          }
          state.HAND_RECOGNITION_DATA = dataToSend;
        }
        return result;
      } catch (e) { return false; }
    }


    const darkUpper = settings.handRecognitionLimitDarkUpper;
    const brightLower = settings.handRecognitionLimitBrightLower;
    const brightUpper = settings.handRecognitionLimitBrightUpper;

    let serialDispatcher = new SerialDispatcher(state.CONNECTION_PARAMS, this.platform, state.SCANNER_VERSION, state.SCANNER_SERIAL);
    await serialDispatcher.write(`hand?du=${darkUpper}&bl=${brightLower}&bu=${brightUpper}`);
    const [status, res] = await serialDispatcher.getResult();
    serialDispatcher = null;
    if (!status) {
      return false;
    }
    try {
      let result = JSON.parse(res).hand === 0;
      if (handOff) {
        result = !result;
      }
      return result;
    } catch (e) { return false; }
  }

  /**
   * Performs an AOX measurement.
   *
   * @returns An observable that emits HttpEvents during the measurement process.
   *                                      - The observable completes when the measurement is finished.
   * @param state state object
   * @param measurementType type of the usb measurement
   */
  doAOXMeasurementObservable(state: StateInterface, measurementType: string): Observable<HttpEvent<any>> {
    let totalBlocks = 5773;
    let command = 'measure?type=aox-prod';
    let index = 0;
    switch (measurementType) {
      case 'full': {
        totalBlocks = 5773;
        command = 'measure?type=aox';
        break;
      }
      case 'front_half': {
        totalBlocks = 2887;
        command = 'measure?type=aox-half';
        break;
      }
      case 'hrv': {
        totalBlocks = 45000;
        command = 'measure?type=hrv';
        break;
      }
      case 'hrv-test': {
        totalBlocks = 5000;
        command = 'measure?type=hrv-test';
        break;
      }
      default: {
        totalBlocks = 5773;
        command = 'measure?type=aox';
        break;
      }
    }
    const foundIndexes: boolean[] = Array(totalBlocks).fill(false);
    const resDict = {};
    let missingArray = [[0, 150]];
    let serialDispatcherInner = new SerialDispatcher(state.CONNECTION_PARAMS, this.platform, state.SCANNER_VERSION, state.SCANNER_SERIAL);
    let goodBlocks = 0;
    let startTS: number;
    const initFunc = async () => {
      if (index > 0) {
        return;
      }
      const serialDispatcherInit =
        new SerialDispatcher(state.CONNECTION_PARAMS, this.platform, state.SCANNER_VERSION, state.SCANNER_SERIAL);
      const major = Number(state.SCANNER_STATUS?.wifi_firmware?.split('.')[0] ?? '0');
      const minor = Number(state.SCANNER_STATUS?.wifi_firmware?.split('.')[1] ?? '0');
      if (major > 9 && minor < 1) {
        await serialDispatcherInit.write('erase-chip');
        const [isOkErase, resultDeleteErase] = await serialDispatcherInit.getResult();
        if (isOkErase) {
          while (true) {
            await serialDispatcherInit.write('status');
            const [isOkLoop, resultLoop] = await serialDispatcherInit.getResult();
            if (isOkLoop) {
              try {
                const resultJson = JSON.parse(resultLoop);
                if (!resultJson.busy) {
                  break;
                }
              } catch (e) {}
            }
            await new Promise((r) => setTimeout(r, 1000));
          }
        }
      } else {
        await serialDispatcherInit.write('delete');
        await serialDispatcherInit.getResult();
      }
      await serialDispatcherInit.write(command);
      await new Promise((r => setTimeout(r, 250)));
      return serialDispatcherInit;
    };

    let serialDispatcher: SerialDispatcher;

    return from(initFunc()).pipe(
      switchMap(async _ => {
        if (index > 0) {
          return index;
        }
        serialDispatcher = _;
        const [isOk, resultRaw] = await serialDispatcher.getResult();
        startTS = Date.now();
        serialDispatcher = null;
        try {
          const resultJson = JSON.parse(resultRaw);
          index = resultJson.measurement_nr;
          return index;
        } catch (e) {
          return 0;
        }
        // return 60;
      }),
      delay(100),
      switchMap(async resultIndex => {
        if (goodBlocks === totalBlocks) {
          return new HttpResponse({ body: missingArray.join('') });
        }

        const currentTS = Date.now();
        const blocksPerSecond = 400;
        const currentEntry = missingArray[0];
        const currentMax = Math.floor(((currentTS - startTS) / 1000) * blocksPerSecond);

        const start = currentEntry[0];
        let range = currentEntry[1];

        if (currentMax <= start) {
          return { type: HttpEventType.DownloadProgress, loaded: (goodBlocks), total: totalBlocks } as HttpProgressEvent;
        }



        if (start + range > currentMax) {
          range = currentMax - start + 1;
        }
        range = Math.min(range, 400);
        await serialDispatcherInner.write(`read-flash?nr=${resultIndex}&block=${start}&range=${range}`, range);
        const [statusInner, resInner] = await serialDispatcherInner.getResult();
        if (statusInner) {
          const { status: statusEval, data: d, goodBlocks: gb, ok } =
            evalJsonWrapper(resInner, resDict, totalBlocks, foundIndexes, command.includes('hrv'));
          if (ok) {
            goodBlocks = gb;
            missingArray = d;
          }
          if (statusEval) {
            serialDispatcherInner = null;
            return new HttpResponse({ body: missingArray.join('') });
          }
        }
        return { type: HttpEventType.DownloadProgress, loaded: (goodBlocks), total: totalBlocks } as HttpProgressEvent;
      }),
      repeat(),
    );
  }

  async getStatus(state: StateInterface): Promise<ScannerStatus> {
    let serialDispatcher = new SerialDispatcher(state.CONNECTION_PARAMS, this.platform, state.SCANNER_VERSION, state.SCANNER_SERIAL);
    // await this.disableUSB(state);
    // await serialDispatcher.write('X\n');
    // await new Promise((r => setTimeout(r, 10)));
    if (state.SCANNER_SERIAL.startsWith('2N')) {
      await serialDispatcher.write('status');
    } else {
      await serialDispatcher.write('get_status');
    }
    const [status, res] = await serialDispatcher.getResult();
    serialDispatcher = null;
    try {
      if (status) {
        return JSON.parse(res);
      } else {
        return;
      }
    } catch (e) {
      return;
    }
  }

  async disconnectScanner(state: StateInterface) {
    console.log('Disconnecting scanner');
    if (this.platform.is('cordova') && this.platform.is('android')) {
      try {
        // await this.serial.close();
        console.log('Scanner disconnected');
      } catch (e) {
        console.log('Scanner could not be disconnected');
        // pass
      }
    }
  }
}
